// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getPlatformSummary } from '../../api/billingApi';
import './platform-billing.css';

/* Platform Billing — /platform/billing (Abon-side, superuser only).
   Every active tenant's Connect state in one view. Tokens only (light + dark),
   matching the styling pass. Data: GET /api/billing/platform-summary/
   (billing_app.services.summary.platform_billing_summary) — real, no sample. */

// The 6 rollup states C emits, in severity order for the table sort.
const STATE_META = {
  live_collecting:        { label: 'Collecting',      cls: 'ok',    rank: 0 },
  active_unverified:      { label: 'Needs verify',    cls: 'warn',  rank: 1 },
  onboarding:             { label: 'Onboarding',      cls: 'info',  rank: 2 },
  allowlisted_not_started:{ label: 'Not started',     cls: 'off',   rank: 3 },
  not_allowlisted:        { label: 'Not allow-listed',cls: 'off',   rank: 4 },
  fieldroutes_billed:     { label: 'FieldRoutes',     cls: 'muted', rank: 5 },
};

const fmtDate = (v) => { if (!v) return '—'; try { return new Date(v).toLocaleDateString(); } catch { return '—'; } };
const sumCounts = (c) => Object.values(c || {}).reduce((a, b) => a + (Number(b) || 0), 0);

function StatTile({ label, value, cls }) {
  return (
    <div className={`pbl-tile ${cls || ''}`}>
      <div className="pbl-tile-val">{value}</div>
      <div className="pbl-tile-label">{label}</div>
    </div>
  );
}

export default function PlatformBilling() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    getPlatformSummary()
      .then((r) => {
        setRows(r.data?.tenants || []); setForbidden(false); setGeneratedAt(new Date());
      })
      .catch((e) => {
        if (e?.status === 403) { setForbidden(true); return; }
        setError(e?.message || 'Could not load platform billing.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(
    () => [...(rows || [])].sort((a, b) =>
      (STATE_META[a.state]?.rank ?? 9) - (STATE_META[b.state]?.rank ?? 9) ||
      sumCounts(b.payment_counts) - sumCounts(a.payment_counts)),
    [rows]);

  const counts = useMemo(() => {
    const c = { collecting: 0, onboarding: 0, needs_verify: 0, not_started: 0 };
    for (const t of (rows || [])) {
      if (t.state === 'live_collecting') c.collecting++;
      else if (t.state === 'onboarding') c.onboarding++;
      else if (t.state === 'active_unverified') c.needs_verify++;
      else if (t.state === 'allowlisted_not_started' || t.state === 'not_allowlisted') c.not_started++;
    }
    return c;
  }, [rows]);

  if (forbidden) return <div className="pbl"><p className="pbl-empty">Platform staff only.</p></div>;
  if (loading && !rows) return <div className="pbl"><p className="pbl-empty">Loading platform billing…</p></div>;

  return (
    <div className="pbl">
      <div className="pbl-head">
        <div>
          <h1>Platform Billing</h1>
          <span className="pbl-sub">Every tenant’s payment state — onboarded, verified, collecting, or stuck.</span>
        </div>
        <div className="pbl-head-right">
          {generatedAt && <span className="pbl-asof">as of {generatedAt.toLocaleTimeString()}</span>}
          <button className="sv2-btn" onClick={load} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
        </div>
      </div>

      {error && <div className="pbl-banner danger">{error} <button className="pbl-retry" onClick={load}>Retry</button></div>}

      <div className="pbl-tiles">
        <StatTile label="Collecting" value={counts.collecting} cls="ok" />
        <StatTile label="Onboarding" value={counts.onboarding} cls="info" />
        <StatTile label="Needs verification" value={counts.needs_verify} cls="warn" />
        <StatTile label="Not started" value={counts.not_started} cls="off" />
      </div>

      <div className="pbl-table-wrap">
        <table className="pbl-table">
          <thead><tr>
            <th>Tenant</th><th>State</th><th>Connect</th>
            <th>Charges</th><th>Payouts</th>
            <th>Key 1</th><th>Key 2 (verified)</th>
            <th className="num">Payments</th>
          </tr></thead>
          <tbody>
            {sorted.map((t) => {
              const meta = STATE_META[t.state] || STATE_META.not_allowlisted;
              const total = sumCounts(t.payment_counts);
              const failed = Number(t.payment_counts?.failed || 0);
              return (
                <tr key={t.tenant_id}>
                  <td className="pbl-name">
                    {t.name} <span className="pbl-id">t{t.tenant_id}</span>
                    {t.is_test_tenant && <span className="pbl-pill muted pbl-test">TEST</span>}
                  </td>
                  <td><span className={`pbl-pill ${meta.cls}`}>{meta.label}</span></td>
                  <td className="pbl-dim">{t.connect_status || '—'}</td>
                  <td>{t.charges_enabled ? <span className="pbl-yes">Yes</span> : <span className="pbl-no">—</span>}</td>
                  <td>{t.payouts_enabled ? <span className="pbl-yes">Yes</span> : <span className="pbl-no">—</span>}</td>
                  <td>{t.key1_allowlisted ? <span className="pbl-yes">Allow-listed</span> : <span className="pbl-no">—</span>}</td>
                  <td>
                    {t.key2_verified
                      ? <span className="pbl-yes">Verified{t.key2_by ? ` · ${t.key2_by}` : ''}{t.key2_at ? ` · ${fmtDate(t.key2_at)}` : ''}</span>
                      : <span className="pbl-no">—</span>}
                  </td>
                  <td className="num">
                    {total || '—'}{failed > 0 && <span className="pbl-failed"> · {failed} failed</span>}
                  </td>
                </tr>
              );
            })}
            {!sorted.length && <tr><td colSpan={8} className="pbl-empty-row">No active tenants.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
